import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useState, useEffect } from "react";
import "./dashboard.css";
import { useNavigate } from "react-router-dom";

// Icons
// Icons
import {
  Description as DescriptionIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";

import StatCard from "../../components/datatable/StatCard";
import DashboardFilter from "../../components/common/DashboardFilter";
import { formatAmount } from "../../utils/format";
import { handleLeadRedirect } from "../../utils/leadRedirect";

import * as XLSX from "xlsx";

// Chart
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// API
import {
  getDashboardStats,
  exportAllLeads,
  exportSingleLead,
} from "../../api/api";

// STATUS CONFIG
import { DASHBOARD_STRINGS, STATUS_CONFIG } from "../../constants/strings";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalApplications: 0,
    approved: 0,
    disbursed: 0,
    pending: 0,
  });

  const [chartData, setChartData] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    product: "",
    status: "",
  });

  // ================= FETCH =================
  const fetchDashboard = async () => {
    try {
      const res = await getDashboardStats(filters);

      setStats({
        totalApplications: res.data.stats?.totalApplications || 0,

        approved: res.data.stats?.approved || 0,

        disbursed: res.data.stats?.disbursed || 0,

        pending: res.data.stats?.pending || 0,
      });

      setChartData(res.data.chartData || []);
      setLeads(res.data.recentLeads || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [filters]);

  // ================= RESET =================
  const handleReset = () => {
    setFilters({
      from: "",
      to: "",
      product: "",
      status: "",
    });
  };

  // ================= EXPORT ALL =================
  const handleExportAll = async () => {
    try {
      const res = await exportAllLeads();

      const allRows: any[] = [];

      res.data.data.forEach((item: any) => {
        const { lead, loan, disbursement, commission } = item;

        allRows.push({
          // 👤 CUSTOMER
          "Lead ID": lead?.leadCode,
          "Customer Name": lead?.customerName,
          "Mobile Number": lead?.mobile,
          Product: lead?.productType,
          "Case Type": lead?.caseType,
          Status: lead?.status,
          "Pending Reason": lead?.pendingReason,

          // 📍 ADDRESS
          "Permanent Address": lead?.address?.permanent,
          "Current Address": lead?.address?.current,

          // 📄 DOCUMENTS
          "Aadhar No": lead?.documents?.aadharNumber,
          "PAN No": lead?.documents?.panNumber,

          // 💰 LOAN
          "Loan Amount (₹)": loan?.loanAmount,
          "Scheme Type": loan?.schemeType,
          "Tenure (Months)": loan?.tenure,
          "ROI (%)": loan?.roi,
          "File Charges (₹)": loan?.fileCharges,
          "Gap Interest (₹)": loan?.gapInterest,
          "Net Loan Amount (₹)": loan?.netAmount,

          // 🏦 DISBURSEMENT
          "Disbursement Amount (₹)": disbursement?.netDisbursement,
          "Disbursement Date": disbursement?.disbursementDate,
          "RTO Charges (₹)": disbursement?.rtoCharges,
          "Insurance Charges (₹)": disbursement?.insuranceCharges,
          "Other Charges (₹)": disbursement?.otherCharges,

          // 💳 PAYMENTS
          "First Payment": disbursement?.firstPayment,
          "Second Payment": disbursement?.secondPayment,
          "Paid To Bank": disbursement?.paidToBank,
          "Paid To Customer": disbursement?.paidToCustomer,

          // 💸 COMMISSION
          "Bank %": commission?.bankPercentage,
          "Bank Amount (₹)": commission?.bankAmount,
          "GST %": commission?.bankGst,
          "GST Amount (₹)": commission?.bankGstAmount,
          "Final Bank Amount (₹)": commission?.bankFinal,
          "Broker Name": commission?.brokerName,
          "Broker Mobile": commission?.brokerMobile,
          "Broker Amount (₹)": commission?.brokerAmount,
          "Profit (₹)": commission?.profit,

          // 📅 META
          "Created Date": new Date(lead?.createdAt).toLocaleString("en-IN"),
        });
      });

      // ================= EXCEL CREATION =================

      const ws = XLSX.utils.json_to_sheet(allRows);

      // 🔥 COLUMN WIDTH AUTO
      const colWidths = Object.keys(allRows[0] || {}).map((key) => ({
        wch: Math.max(key.length + 5, 20),
      }));
      ws["!cols"] = colWidths;

      // 🔥 DATE FORMAT FIX
      allRows.forEach((row, i) => {
        const cell = ws[`AE${i + 2}`]; // Created Date column (adjust if needed)
        if (cell) {
          cell.z = "dd-mm-yyyy";
        }
      });

      // 🔥 HEADER STYLE (BOLD)
      const range = XLSX.utils.decode_range(ws["!ref"] || "");

      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true },
          };
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "All Leads");

      XLSX.writeFile(wb, "Bank_Level_Report.xlsx");
    } catch (err) {
      console.log(err);
    }
  };

  // ================= EXPORT SINGLE =================
  const handleExportSingle = async (id: string) => {
    try {
      const res = await exportSingleLead(id);

      const ws = XLSX.utils.json_to_sheet([res.data.data]);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Lead Full Data");

      XLSX.writeFile(wb, `${res.data.data.leadCode || "Lead"}_Report.xlsx`);
    } catch (err) {
      console.log(err);
    }
  };

  // ================= CARDS =================

  const cards = [
    {
      label: "Applications",
      value: formatAmount(stats.totalApplications),
      icon: <DescriptionIcon />,
      color: "#6366f1",
      bg: "#eef2ff",
    },

    {
      label: "Approved",
      value: formatAmount(stats.approved),
      icon: <TrendingUpIcon />,
      color: "#22c55e",
      bg: "#ecfdf5",
    },

    {
      label: "Disbursed",
      value: formatAmount(stats.disbursed),
      icon: <AccountBalanceWalletIcon />,
      color: "#0ea5e9",
      bg: "#f0f9ff",
    },

    {
      label: "Pending Cases",
      value: formatAmount(stats.pending),
      icon: <AccessTimeIcon />,
      color: "#f59e0b",
      bg: "#fffbeb",
    },
  ];

  return (
    <Box className="dashboard-root">
      {/* FILTER */}
      <DashboardFilter
        filters={filters}
        setFilters={setFilters}
        onReset={handleReset}
      />

      {/* CARDS */}
      <Box className="cards">
        {cards.map((item, i) => (
          <StatCard
            key={i}
            label={item.label}
            value={item.value}
            icon={item.icon}
            color={item.color}
            bg={item.bg}
          />
        ))}
      </Box>

      {/* TABLE + CHART */}
      <Box className="main-section">
        {/* TABLE */}
        <Paper className="table">
          <Box className="table-header">
            <Typography>{DASHBOARD_STRINGS.RECENT_LEADS}</Typography>

            <Button variant="outlined" size="small" onClick={handleExportAll}>
              Export All
            </Button>
          </Box>

          {/* HEADER */}
          <Box className="table-head">
            <Box>Lead ID</Box>
            <Box>Name</Box>
            <Box>Mobile</Box>
            <Box>Product</Box>
            <Box>Reason</Box>
            <Box>Status</Box>
            <Box>Action</Box>
          </Box>

          {/* ROWS */}
          {leads.length > 0 ? (
            leads.map((lead) => {
              const statusKey = (lead.status?.toUpperCase() ||
                "PENDING") as keyof typeof STATUS_CONFIG;

              const statusObj =
                STATUS_CONFIG[statusKey] || STATUS_CONFIG.PENDING;

              return (
                <Box
                  key={lead._id}
                  className="row"
                  onClick={() => handleLeadRedirect(lead, navigate)}
                  sx={{ cursor: "pointer" }}
                >
                  <Box>{lead.leadCode || "-"}</Box>
                  <Box>{lead.customerName}</Box>
                  <Box>{lead.mobile}</Box>
                  <Box>{lead.productType}</Box>
                  <Box>
                    {lead.status === "pending"
                      ? lead.pendingReason || "-"
                      : "-"}
                  </Box>
                  <Box>
                    <Chip
                      label={statusObj.label}
                      size="small"
                      sx={{
                        background: statusObj.bg,
                        color: statusObj.color,
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                  {/* ACTIONS */}
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: "flex", gap: 1 }}
                  >
                    {/* VIEW */}
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleLeadRedirect(lead, navigate)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* EXPORT */}
                    <Tooltip title="Export">
                      <IconButton
                        size="small"
                        onClick={() => handleExportSingle(lead._id)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          ) : (
            <Box sx={{ p: 2, textAlign: "center" }}>No data found</Box>
          )}
        </Paper>

        {/* CHART */}
        <Paper className="chart">
          <Typography className="chart-title">
            {DASHBOARD_STRINGS.CHART}
          </Typography>

          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <ChartTooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#6366f1"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
